import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  Divider,
  Link,
} from '@mui/material';
import { textStyles } from '../styles/textstyle';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { GuestOrderResponse, UserOrderResponse, GuestOrderRequest } from '../types/shop';
import { OrderDetails, OrderDetailsResponse } from '../types/order';
import { useCart } from '../cart/CartManager';
import { useAuth } from '../auth';

interface CheckoutResultProps {
  status: 'processing' | 'success' | 'fail';
  orderResponse?: GuestOrderResponse | UserOrderResponse | null;
  orderRequest?: GuestOrderRequest | null;
  orderDetails?: OrderDetails | OrderDetailsResponse | null;
  onBackToHome?: () => void;
  onRetry?: () => void;
  embedded?: boolean; // If true, component is embedded in checkout page
}

const CheckoutResult: React.FC<CheckoutResultProps> = ({
  status,
  orderResponse,
  orderRequest,
  orderDetails,
  onBackToHome,
  onRetry,
  embedded = false
}) => {
  const navigate = useNavigate();
  const { clearCart } = useCart();
  const { isAuthenticated } = useAuth();

  // Clear cart only on success
  React.useEffect(() => {
    if (orderResponse && status === 'success') {
      clearCart();
    }
  }, [orderResponse, status]);

  // Helper function to determine if it's a user order response
  const isUserOrder = (data: GuestOrderResponse | UserOrderResponse): data is UserOrderResponse => {
    return data && 'success' in data && data.success && 'order' in data && typeof data.order === 'object';
  };

  // Helper function to safely get payment method name
  const getPaymentMethodName = (paymentMethod: unknown): string => {
    if (typeof paymentMethod === 'string') {
      return paymentMethod;
    }
    if (paymentMethod && typeof paymentMethod === 'object' && paymentMethod !== null && 'name' in paymentMethod) {
      return (paymentMethod as { name: string }).name;
    }
    return '-';
  };

  // Helper function to determine if order should show PAID status for Stripe payments
  const getDisplayStatus = (orderDetailsRaw: { paymentMethod?: string; paidAt?: string | null; status?: string; paymentIntentId?: string | null }) => {
    // Check if payment method is Stripe
    const isStripePayment = orderDetailsRaw.paymentMethod?.toLowerCase().includes('stripe');
    
    if (isStripePayment) {
      // For Stripe payments, show PAID if any of these conditions are met:
      // 1. paidAt field exists (backend updated after successful payment)
      // 2. order status indicates payment success
      // 3. paymentIntentId exists (indicates Stripe payment was processed)
      if (orderDetailsRaw.paidAt || 
          orderDetailsRaw.status?.toLowerCase() === 'paid' ||
          orderDetailsRaw.paymentIntentId) {
        return 'PAID';
      }
    }
    
    return orderDetailsRaw.status;
  };

  // Helper function to get order details regardless of type
  const getOrderDetails = () => {
    // If we have orderDetails from the API, use that (most complete data)
    if (orderDetails) {
      // Check if orderDetails is the new API response structure
      if ('success' in orderDetails && orderDetails.success && 'order' in orderDetails) {
        const orderDetailsResponse = orderDetails as unknown as OrderDetailsResponse;
        return {
          orderNumber: orderDetailsResponse.order.orderNumber,
          status: orderDetailsResponse.order.status,
          totalAmount: orderDetailsResponse.order.finalTotal,
          currency: orderDetailsResponse.currency,
          estimatedDeliveryDate: null, // Not in the new API response
          trackingNumber: orderDetailsResponse.order.trackingNumber,
          shippingCarrier: orderDetailsResponse.order.shippingCarrier,
          shipperName: `${orderDetailsResponse.shippingAddress.firstName} ${orderDetailsResponse.shippingAddress.lastName}`,
          paymentMethod: orderDetailsResponse.paymentMethod?.name || '-',
          shippingTo: orderDetailsResponse.shippingAddress.country,
          shippingCompany: orderDetailsResponse.shippingMethod.name,
          subtotal: orderDetailsResponse.calculation.subtotal,
          totalDiscount: orderDetailsResponse.calculation.totalDiscount,
          totalItemsValue: orderDetailsResponse.calculation.totalItemsValue,
          shippingFee: orderDetailsResponse.calculation.shippingAmount,
          items: orderDetailsResponse.items,
          paidAt: orderDetailsResponse.order.paidAt,
          paymentIntentId: orderDetailsResponse.order.paymentIntentId,
          fulfillmentStatus: orderDetailsResponse.order.fulfillmentStatus,
        };
      } else {
        // Legacy OrderDetails structure
        const legacyOrderDetails = orderDetails as OrderDetails;
        return {
          orderNumber: legacyOrderDetails.orderNumber,
          status: legacyOrderDetails.status,
          totalAmount: legacyOrderDetails.totalAmount,
          currency: legacyOrderDetails.currency,
          estimatedDeliveryDate: null, // Not in the new API response
          trackingNumber: null, // Not in the new API response
          shipperName: `${legacyOrderDetails.shippingAddress.firstName} ${legacyOrderDetails.shippingAddress.lastName}`,
          paymentMethod: legacyOrderDetails.paymentMethod || '-',
          shippingTo: legacyOrderDetails.shippingAddress.country,
          shippingCompany: legacyOrderDetails.shippingMethod.name,
          subtotal: legacyOrderDetails.subtotalAmount,
          totalDiscount: legacyOrderDetails.discountAmount,
          totalItemsValue: legacyOrderDetails.subtotalAmount,
          shippingFee: legacyOrderDetails.shippingAmount,
          items: legacyOrderDetails.items,
          paidAt: legacyOrderDetails.paidAt,
          paymentIntentId: legacyOrderDetails.paymentIntentId,
          fulfillmentStatus: legacyOrderDetails.fulfillmentStatus,
        };
      }
    }
    
    // Fallback to original orderResponse data
    if (!orderResponse) return null;
    
    // For user order with new structure
    if (isUserOrder(orderResponse)) {
      return {
        orderNumber: orderResponse.order.orderNumber,
        status: orderResponse.order.status,
        totalAmount: orderResponse.order.finalTotal,
        currency: orderResponse.currency,
        estimatedDeliveryDate: null, // Not in the new API response
        trackingNumber: orderResponse.order.trackingNumber,
        shippingCarrier: orderResponse.order.shippingCarrier,
        shipperName: orderResponse.shippingAddress ? `${orderResponse.shippingAddress.firstName} ${orderResponse.shippingAddress.lastName}` : '-',
        paymentMethod: orderResponse.paymentMethod?.name || '-',
        shippingTo: orderResponse.shippingAddress?.country || '-',
        shippingCompany: '-', // Not in the new API response
        subtotal: orderResponse.calculation?.subtotal,
        totalDiscount: orderResponse.calculation?.totalDiscount,
        totalItemsValue: orderResponse.calculation?.totalItemsValue,
        shippingFee: orderResponse.calculation?.shippingCost,
        items: orderResponse.items,
        validatedDiscounts: orderResponse.validatedDiscounts,
      };
    } else {
      // For guest order
      return {
        orderNumber: orderResponse.orderNumber,
        status: orderResponse.status,
        totalAmount: orderResponse.totalAmount,
        currency: orderResponse.currency,
        estimatedDeliveryDate: orderResponse.estimatedDeliveryDate,
        trackingNumber: orderResponse.trackingNumber,
        shipperName: orderRequest?.shippingAddress ? `${orderRequest.shippingAddress.firstName} ${orderRequest.shippingAddress.lastName}` : '-',
        paymentMethod: orderRequest?.paymentMethodId || 'Stripe',
        shippingTo: orderRequest?.shippingAddress ? orderRequest.shippingAddress.country : '-',
        shippingCompany: '-',
        subtotal: '-',
        totalDiscount: '-',
        totalItemsValue: '-',
        shippingFee: '-',
      };
    }
  };

  const handleBackToHome = () => {
    if (onBackToHome) {
      onBackToHome();
    } else {
      navigate('/');
    }
  };

  const handleRetry = () => {
    if (onRetry) {
      onRetry();
    } else {
      if (!isAuthenticated) {
        // Redirect to login page, preserving the cart page as the intended destination
        navigate('/sign-in', { state: { from: { pathname: '/cart' } } });
        return;
      }
      navigate('/checkout');
    }
  };

  // --- PROCESSING STATE ---
  if (status === 'processing') {
    return (
      <Box 
        p={embedded ? 2 : 4} 
        textAlign="center" 
        sx={{ 
          minHeight: embedded ? 'auto' : '80vh', 
          display: 'flex', 
          flexDirection: 'column', 
          justifyContent: 'center', 
          alignItems: 'center' 
        }}
      >
        <img src="/icons/ic_order_processing.svg" alt="Processing" style={{ width: 80, height: 80, marginBottom: 24 }} />
        <Typography sx={textStyles.checkoutTitle} mb={2}>
          Processing your Payment
        </Typography>
        <Typography sx={textStyles.checkoutSubtitle} mb={3}>
          This may take few minutes
        </Typography>
        <Box
          sx={{
            border: '1px solid #E1DACA',
            background: '#FCFAF6',
            borderRadius: 2,
            px: 3,
            py: 1.5,
            display: 'inline-block',
            color: '#6B6B6B',
            fontWeight: 500,
            mb: 4,
          }}
        >
          Please do not press back or switch to another apps
        </Box>
        <Button
          variant="contained"
          color="inherit"
          fullWidth
          onClick={handleBackToHome}
          sx={{
            bgcolor: '#b48b6a',
            color: '#fff',
            fontWeight: 700,
            fontSize: 16,
            py: 1.5,
            borderRadius: 2,
            boxShadow: 'none',
            maxWidth: 240,
            mx: 'auto',
            '&:hover': {
              bgcolor: '#a67c5a',
            },
          }}
        >
          Back to Home
        </Button>
      </Box>
    );
  }

  // --- FAIL STATE ---
  if (status === 'fail') {
    return (
      <Box 
        p={embedded ? 2 : 4} 
        textAlign="center" 
        sx={{ 
          minHeight: embedded ? 'auto' : '80vh', 
          display: 'flex', 
          flexDirection: 'column', 
          justifyContent: 'center', 
          alignItems: 'center' 
        }}
      >
        <img src="/icons/ic_payment_failed.svg" alt="Payment Failed" style={{ width: 80, height: 80, marginBottom: 24 }} />
        <Typography sx={textStyles.checkoutTitle} mb={2}>
          Payment Failed
        </Typography>
        <Typography sx={textStyles.checkoutSubtitle} mb={3}>
          Please try again or use a different payment method.
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexDirection: embedded ? 'column' : 'row' }}>
          <Button
            variant="contained"
            color="inherit"
            onClick={handleRetry}
            sx={{
              bgcolor: '#b48b6a',
              color: '#fff',
              fontWeight: 700,
              fontSize: 16,
              py: 1.5,
              borderRadius: 2,
              boxShadow: 'none',
              maxWidth: 240,
              '&:hover': {
                bgcolor: '#a67c5a',
              },
            }}
          >
            Try Again
          </Button>
          <Button
            variant="outlined"
            color="inherit"
            onClick={handleBackToHome}
            sx={{
              borderColor: '#b48b6a',
              color: '#b48b6a',
              fontWeight: 700,
              fontSize: 16,
              py: 1.5,
              borderRadius: 2,
              maxWidth: 240,
              '&:hover': {
                borderColor: '#a67c5a',
                color: '#a67c5a',
              },
            }}
          >
            Back to Home
          </Button>
        </Box>
      </Box>
    );
  }

  // --- SUCCESS STATE ---
  const orderDetailsRaw = getOrderDetails();
  
  if (!orderDetailsRaw) {
    return (
      <Box p={embedded ? 2 : 4} textAlign="center">
        <Typography variant="h6" mb={2}>
          No order data found.
        </Typography>
        <Button
          variant="contained"
          onClick={handleBackToHome}
          sx={{
            bgcolor: '#b48b6a',
            color: '#fff',
            fontWeight: 700,
            fontSize: 16,
            py: 1.5,
            borderRadius: 2,
            boxShadow: 'none',
            '&:hover': {
              bgcolor: '#a67c5a',
            },
          }}
        >
          Back to Home
        </Button>
      </Box>
    );
  }

  return (
    <Box p={embedded ? 2 : 4} textAlign="center" sx={{ minHeight: embedded ? 'auto' : '80vh' }}>
      <CheckCircleIcon sx={{ fontSize: 80, color: '#4CAF50', mb: 2 }} />
      <Typography sx={textStyles.checkoutTitle} mb={2}>
        Order Confirmed!
      </Typography>
      <Typography sx={textStyles.checkoutSubtitle} mb={4}>
        Thank you for your order. We'll send you an email confirmation shortly.
      </Typography>

      <Paper
        variant="outlined"
        sx={{
          maxWidth: 600,
          mx: 'auto',
          p: 3,
          backgroundColor: '#FCFAF6',
          border: '1px solid #E1DACA',
          borderRadius: 2,
          mb: 4,
        }}
      >
        <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
          Order Details
        </Typography>

        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 3 }}>
          <Box>
            <Typography sx={{ fontSize: 14, color: '#6B6B6B', mb: 0.5 }}>
              Order Number
            </Typography>
            <Typography sx={{ fontWeight: 600 }}>
              {orderDetailsRaw.orderNumber}
            </Typography>
          </Box>
          <Box>
            <Typography sx={{ fontSize: 14, color: '#6B6B6B', mb: 0.5 }}>
              Status
            </Typography>
            <Typography sx={{ fontWeight: 600, color: '#4CAF50' }}>
              {getDisplayStatus(orderDetailsRaw)}
            </Typography>
          </Box>
          <Box>
            <Typography sx={{ fontSize: 14, color: '#6B6B6B', mb: 0.5 }}>
              Total Amount
            </Typography>
            <Typography sx={{ fontWeight: 600 }}>
              {orderDetailsRaw.currency} {orderDetailsRaw.totalAmount ? orderDetailsRaw.totalAmount.toFixed(2) : '0.00'}
            </Typography>
          </Box>
          <Box>
            <Typography sx={{ fontSize: 14, color: '#6B6B6B', mb: 0.5 }}>
              Payment Method
            </Typography>
            <Typography sx={{ fontWeight: 600 }}>
              {getPaymentMethodName(orderDetailsRaw.paymentMethod)}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 3 }} />

                <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
          Shipping Information
        </Typography>

        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 3 }}>
          <Box>
            <Typography sx={{ fontSize: 14, color: '#6B6B6B', mb: 0.5 }}>
              Shipping Address
            </Typography>
            <Typography sx={{ fontWeight: 600, lineHeight: 1.4 }}>
              {(() => {
                if (orderDetails && 'success' in orderDetails && orderDetails.success) {
                  const address = orderDetails.shippingAddress;
                  return (
                    <>
                      {address.firstName} {address.lastName}<br />
                      {address.address}
                      {address.apartment && `, ${address.apartment}`}<br />
                      {address.city}, {address.state} {address.zipCode}<br />
                      {address.country}
                    </>
                  );
                } else if (orderDetails && 'shippingAddress' in orderDetails) {
                  const address = orderDetails.shippingAddress;
                  return (
                    <>
                      {address.firstName} {address.lastName}<br />
                      {address.address}
                      {address.apartment && `, ${address.apartment}`}<br />
                      {address.city}, {address.state} {address.zipCode}<br />
                      {address.country}
                    </>
                  );
                } else if (orderResponse && isUserOrder(orderResponse) && orderResponse.shippingAddress) {
                  const address = orderResponse.shippingAddress;
                  return (
                    <>
                      {address.firstName} {address.lastName}<br />
                      {address.address}
                      {address.apartment && `, ${address.apartment}`}<br />
                      {address.city}, {address.state} {address.zipCode}<br />
                      {address.country}
                    </>
                  );
                } else if (orderRequest && orderRequest.shippingAddress) {
                  const address = orderRequest.shippingAddress;
                  return (
                    <>
                      {address.firstName} {address.lastName}<br />
                      {address.address}
                      {address.apartment && `, ${address.apartment}`}<br />
                      {address.city}, {address.state} {address.zipCode}<br />
                      {address.country}
                    </>
                  );
                }
                return orderDetailsRaw.shippingTo || '-';
              })()}
            </Typography>
          </Box>
          <Box>
            <Typography sx={{ fontSize: 14, color: '#6B6B6B', mb: 0.5 }}>
              Estimated Delivery
            </Typography>
            <Typography sx={{ fontWeight: 600 }}>
              {orderDetailsRaw.estimatedDeliveryDate || 'TBD'}
            </Typography>
          </Box>
          {orderDetailsRaw.shippingCarrier && (
            <Box>
              <Typography sx={{ fontSize: 14, color: '#6B6B6B', mb: 0.5 }}>
                Shipping Carrier
              </Typography>
              <Typography sx={{ fontWeight: 600 }}>
                {orderDetailsRaw.shippingCarrier}
              </Typography>
            </Box>
          )}
          {orderDetailsRaw.trackingNumber && (
            <Box>
              <Typography sx={{ fontSize: 14, color: '#6B6B6B', mb: 0.5 }}>
                Tracking Number
              </Typography>
              <Typography sx={{ fontWeight: 600 }}>
                {orderDetailsRaw.trackingNumber}
              </Typography>
              <Link
                href={`https://htm.sf-express.com/hk/en/dynamic_function/waybill/?waybillNo=${orderDetailsRaw.trackingNumber}`}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: '#1976D2',
                  textDecoration: 'underline',
                  fontSize: '14px',
                  fontWeight: 500,
                  cursor: 'pointer',
                  display: 'block',
                  mt: 0.5,
                  '&:hover': {
                    color: '#1565C0',
                    textDecoration: 'none',
                  },
                }}
              >
                Click to Track
              </Link>
            </Box>
          )}
        </Box>

        <Button
          variant="contained"
          color="inherit"
          fullWidth
          onClick={handleBackToHome}
          sx={{
            bgcolor: '#b48b6a',
            color: '#fff',
            fontWeight: 700,
            fontSize: 16,
            py: 1.5,
            borderRadius: 2,
            boxShadow: 'none',
            '&:hover': {
              bgcolor: '#a67c5a',
            },
          }}
        >
          Continue Shopping
        </Button>
      </Paper>
    </Box>
  );
};

export default CheckoutResult; 